import { AnimatePresence, motion } from 'motion/react';
import { cn } from './util';

export type ConnectionStatus = 'online' | 'reconnecting' | 'offline';

export interface ConnectionBadgeProps {
  status: ConnectionStatus;
  /** Compact dot-only badge while online (default true): only problems get a loud banner. */
  quietWhenOnline?: boolean;
  /** While quietly online, show only the dot below `sm` (crowded phone headers). */
  phoneDot?: boolean;
  className?: string;
}

const TEXT: Record<ConnectionStatus, string> = {
  online: 'Bağlı',
  reconnecting: 'Bağlantı koptu, yeniden bağlanılıyor…',
  offline: 'Çevrimdışı — internet bağlantını kontrol et',
};

/** Connection status pill: green dot when fine, a loud yellow/red banner when not. */
export function ConnectionBadge({
  status,
  quietWhenOnline = true,
  phoneDot = false,
  className,
}: ConnectionBadgeProps) {
  const quiet = status === 'online' && quietWhenOnline;
  return (
    <div role="status" aria-live="polite" className={cn('inline-flex', className)}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={status}
          initial={{ y: -12, opacity: 0, rotate: -3 }}
          animate={{ y: 0, opacity: 1, rotate: status === 'online' ? 0 : -1.5 }}
          exit={{ y: -8, opacity: 0, transition: { duration: 0.12 } }}
          transition={{ type: 'spring', stiffness: 500, damping: 26 }}
          className={cn(
            'inline-flex items-center gap-2 border-ink font-extrabold',
            quiet
              ? 'border-2 bg-white px-2 py-0.5 text-[12px]'
              : 'border-3 px-3 py-1 text-[14px] shadow-ink-sm',
            status === 'reconnecting' && 'bg-yellow',
            status === 'offline' && 'bg-team-b text-white',
            status === 'online' && !quiet && 'bg-jury-tint',
          )}
        >
          <span className="relative flex size-3 items-center justify-center">
            {status === 'reconnecting' ? (
              <span className="animate-spin-slow size-3 rounded-full border-2 border-ink border-t-transparent" />
            ) : (
              <span
                className={cn(
                  'relative size-2.5 rounded-full border-2 border-ink',
                  status === 'online' ? 'bg-jury' : 'bg-white',
                )}
              />
            )}
          </span>
          {quiet && phoneDot ? (
            <span className="max-sm:sr-only">{TEXT[status]}</span>
          ) : (
            TEXT[status]
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
